import { Component } from '@theme/component';
import { ThemeEvents, VariantUpdateEvent, ZoomMediaSelectedEvent, SlideshowSelectEvent } from '@theme/events';

/**
 * A custom element that renders a media gallery.
 *
 * @typedef {object} Refs
 * @property {import('./zoom-dialog').ZoomDialog} [zoomDialogComponent] - The zoom dialog component.
 * @property {import('./slideshow').Slideshow} [slideshow] - The slideshow component.
 * @property {HTMLElement[]} [media] - The media elements.
 *
 * @extends Component<Refs>
 */
export class MediaGallery extends Component {
  connectedCallback() {
    super.connectedCallback();

    const { signal } = this.#controller;
    const target = this.closest('.shopify-section, dialog');

    target?.addEventListener(ThemeEvents.variantUpdate, this.#handleVariantUpdate, { signal });
    this.refs.zoomDialogComponent?.addEventListener(ThemeEvents.zoomMediaSelected, this.#handleZoomMediaSelected, {
      signal,
    });

    // Escuchar cambios de slide para pausar videos
    this.refs.slideshow?.addEventListener(SlideshowSelectEvent.eventName, this.#handleSlideshowSelect, { signal });

    // 👇 Inicializar el efecto zoom dinámico en las imágenes del grid
    this.initImageHoverZoom();

    // 👇 Configurar IntersectionObserver para autoplay de videos en grid
    this.#setupGridAutoplay();
  }

  #controller = new AbortController();

  disconnectedCallback() {
    super.disconnectedCallback();
    this.#controller.abort();

    // Limpiar el observer de autoplay
    if (this.#gridAutoplayObserver) {
      this.#gridAutoplayObserver.disconnect();
      this.#gridAutoplayObserver = null;
    }
  }

  /**
   * Inicializa el efecto de zoom en las imágenes del grid (.product-media > img)
   * Solo se activa en desktop y con click
   */
  initImageHoverZoom() {
    // Solo ejecutar en desktop (min-width: 750px)
    const isDesktop = window.matchMedia('(min-width: 750px)').matches;
    if (!isDesktop) return;

    // Ocultar todos los botones de zoom
    const zoomButtons = this.querySelectorAll('li.product-media-container > button.product-media-container__zoom-button');
    zoomButtons.forEach(btn => {
      btn.style.display = 'none';
    });

    const productImages = this.querySelectorAll('li.product-media-container  .product-media > img');

    productImages.forEach((img) => {
      // Configuración base por si no existe en CSS
      img.style.transition = 'transform 0.3s ease, transform-origin 0.1s ease';
      img.style.cursor = 'zoom-in';

      let isZoomed = false;

      // Función para activar/desactivar zoom con click
      const handleClick = (e) => {
        e.preventDefault();
        e.stopPropagation();

        isZoomed = !isZoomed;

        if (isZoomed) {
          img.style.transform = 'scale(2.5)';
          img.style.cursor = 'zoom-out';

          // Calcular posición inicial del zoom basado en donde se hizo click
          const rect = img.getBoundingClientRect();
          const x = ((e.clientX - rect.left) / rect.width) * 100;
          const y = ((e.clientY - rect.top) / rect.height) * 100;
          img.style.transformOrigin = `${x}% ${y}%`;
        } else {
          img.style.transform = 'scale(1)';
          img.style.cursor = 'zoom-in';
          img.style.transformOrigin = 'center center';
        }
      };

      // Función para seguir el mouse cuando está zoomed
      const handleMouseMove = (e) => {
        if (!isZoomed) return;

        const rect = img.getBoundingClientRect();
        const x = ((e.clientX - rect.left) / rect.width) * 100;
        const y = ((e.clientY - rect.top) / rect.height) * 100;
        img.style.transformOrigin = `${x}% ${y}%`;
      };

      // Función para desactivar zoom al salir de la imagen
      const handleMouseLeave = () => {
        if (isZoomed) {
          isZoomed = false;
          img.style.transform = 'scale(1)';
          img.style.cursor = 'zoom-in';
          img.style.transformOrigin = 'center center';
        }
      };

      img.addEventListener('click', handleClick);
      img.addEventListener('mousemove', handleMouseMove);
      img.addEventListener('mouseleave', handleMouseLeave);
    });
  }

  /**
   * Configura IntersectionObserver para reproducir automáticamente videos con autoplay
   * cuando se hacen visibles en el grid
   */
  #setupGridAutoplay() {
    // Solo aplicar en presentación grid
    if (this.presentation !== 'grid') return;

    const autoplayVideos = this.querySelectorAll('deferred-media[autoplay]');
    if (!autoplayVideos.length) return;

    // Crear un IntersectionObserver para detectar cuando el video es visible
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          const video = entry.target;

          if (entry.isIntersecting && entry.intersectionRatio > 0.5) {
            // El video es visible, reproducirlo
            if (video.playMedia && !video.isPlaying) {
              video.playMedia();
            }
          } else {
            // El video no es visible, pausarlo
            if (video.pauseMedia && video.isPlaying) {
              video.pauseMedia();
            }
          }
        });
      },
      {
        threshold: 0.5, // El video debe estar al menos 50% visible
        root: null, // Usar el viewport como root
      }
    );

    // Observar todos los videos con autoplay
    autoplayVideos.forEach((video) => {
      observer.observe(video);
    });

    // Guardar el observer para limpiarlo después
    this.#gridAutoplayObserver = observer;
  }

  #gridAutoplayObserver = null;

  /**
   * Handles a variant update event by replacing the current media gallery with a new one.
   *
   * @param {VariantUpdateEvent} event - The variant update event.
   */
  #handleVariantUpdate = (event) => {
    const source = event.detail.data.html;
    if (!source) return;

    const newMediaGallery = source.querySelector('media-gallery');
    if (!newMediaGallery) return;

    this.replaceWith(newMediaGallery);
  };

  /**
   * Handles the 'zoom-media:selected' event.
   * @param {ZoomMediaSelectedEvent} event - The zoom-media:selected event.
   */
  #handleZoomMediaSelected = async (event) => {
    this.slideshow?.select(event.detail.index, undefined, { animate: false });
  };

  /**
   * Handles slideshow slide changes to pause videos and autoplay videos with autoplay attribute
   * @param {SlideshowSelectEvent} event - The slideshow select event.
   */
  #handleSlideshowSelect = (event) => {
    // Pausar todos los videos cuando se cambia de slide
    const allVideos = this.querySelectorAll('deferred-media');
    allVideos.forEach((video) => {
      if (video.pauseMedia) {
        video.pauseMedia();
      }
    });

    // Si el nuevo slide tiene un video con autoplay, reproducirlo
    if (event.detail && event.detail.slide) {
      const currentSlide = event.detail.slide;
      const autoplayVideo = currentSlide.querySelector('deferred-media[autoplay]');

      if (autoplayVideo && autoplayVideo.playMedia) {
        // Esperar un poco para asegurar que el slide esté visible
        setTimeout(() => {
          autoplayVideo.playMedia();
        }, 100);
      }
    }
  };

  /**
   * Zooms the media gallery.
   *
   * @param {number} index - The index of the media to zoom.
   * @param {PointerEvent} event - The pointer event.
   */
  zoom(index, event) {
    this.refs.zoomDialogComponent?.open(index, event);
  }

  get slideshow() {
    return this.refs.slideshow;
  }

  get media() {
    return this.refs.media;
  }

  get presentation() {
    return this.dataset.presentation;
  }
}

if (!customElements.get('media-gallery')) {
  customElements.define('media-gallery', MediaGallery);
}
